import type { ModerationConfig, ModerationScanner } from '@planuze/admin-contracts';
import { isRequiredScannerRuleId } from '@planuze/config';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  InlineAlert,
  Input,
  Label,
  Switch,
} from '@planuze/ui';
import { useState } from 'react';
import { useModerationScannerContent } from '../../content/sections/moderation-scanner.js';
import { LabelWithTooltip } from '../ui/label-with-tooltip.js';
import {
  heuristicFields as buildHeuristicFields,
  limitFields as buildLimitFields,
  type NumericFieldDescriptor,
  SEVERITIES,
  type Severity,
} from './moderation-config-fields.js';
import { describeRule } from './rule-catalog.js';

/* Form da config de moderação (ADR-0471): kill-switch do fastpath + parâmetros do scanner.
   Edita uma cópia local do config live; Salvar faz o PUT (read-modify-write no config-service).
   Só MANAGE_ROLES edita (`canManage`); os demais veem o valor efetivo em modo leitura.
   Campos agrupados por tema em cards (fastpath, scanner, limites, heurísticas, regras);
   cada rótulo carrega um tooltip explicando o efeito real do campo. */

function NumericField({
  descriptor,
  value,
  disabled,
  onChange,
  fieldAriaLabel,
}: {
  readonly descriptor: NumericFieldDescriptor;
  readonly value: number;
  readonly disabled: boolean;
  readonly onChange: (n: number) => void;
  readonly fieldAriaLabel: (label: string) => string;
}) {
  const id = `scanner-${descriptor.key}`;
  return (
    <div className="space-y-1.5">
      <LabelWithTooltip
        htmlFor={id}
        tooltip={descriptor.tooltip}
        ariaLabel={fieldAriaLabel(descriptor.label)}
      >
        {descriptor.label}
      </LabelWithTooltip>
      <Input
        id={id}
        type="number"
        min={0}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function ModerationConfigForm({
  config,
  canManage,
  saving,
  onSave,
}: {
  readonly config: ModerationConfig;
  readonly canManage: boolean;
  readonly saving: boolean;
  readonly onSave: (next: ModerationConfig) => void;
}) {
  const [fastpathEnabled, setFastpathEnabled] = useState(config.fastpathEnabled);
  const [scanner, setScanner] = useState<ModerationScanner>(config.scanner);
  const disabled = !canManage || saving;
  const copy = useModerationScannerContent();
  const limitFields = buildLimitFields(copy);
  const heuristicFields = buildHeuristicFields(copy);

  const patchScanner = (patch: Partial<ModerationScanner>): void =>
    setScanner((prev) => ({ ...prev, ...patch }));

  const toggleFailOn = (sev: Severity): void =>
    patchScanner({
      failOn: scanner.failOn.includes(sev)
        ? scanner.failOn.filter((s) => s !== sev)
        : [...scanner.failOn, sev],
    });

  const toggleRule = (id: string, enabled: boolean): void =>
    patchScanner({ rules: scanner.rules.map((r) => (r.id === id ? { ...r, enabled } : r)) });

  const renderNumericGroup = (fields: readonly NumericFieldDescriptor[]) => (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((f) => (
        <NumericField
          key={f.key}
          descriptor={f}
          value={scanner[f.key] as number}
          disabled={disabled}
          onChange={(n) => patchScanner({ [f.key]: n } as Partial<ModerationScanner>)}
          fieldAriaLabel={copy.form.fieldAriaLabel}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{copy.form.fastpathCardTitle}</CardTitle>
          <CardDescription>{copy.form.fastpathCardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 text-sm">
            <Switch
              id="fastpath-enabled"
              checked={fastpathEnabled}
              onCheckedChange={setFastpathEnabled}
              disabled={disabled}
            />
            <LabelWithTooltip
              htmlFor="fastpath-enabled"
              tooltip={copy.tooltips.fastpath}
              ariaLabel={copy.form.fastpathAriaLabel}
            >
              {copy.form.fastpathLabel}{' '}
              {fastpathEnabled ? copy.form.fastpathOnWord : copy.form.fastpathOffWord}
            </LabelWithTooltip>
          </div>
          {!fastpathEnabled && (
            <InlineAlert variant="warning" className="mt-3">
              {copy.form.fastpathOffWarning}
            </InlineAlert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{copy.form.scannerCardTitle}</CardTitle>
          <CardDescription>{copy.form.scannerCardDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <Switch
              id="scanner-enabled"
              checked={scanner.enabled}
              onCheckedChange={(c) => patchScanner({ enabled: c })}
              disabled={disabled}
            />
            <LabelWithTooltip
              htmlFor="scanner-enabled"
              tooltip={copy.tooltips.scannerEnabled}
              ariaLabel={copy.form.scannerAriaLabel}
            >
              {copy.form.scannerLabel}{' '}
              {scanner.enabled ? copy.form.scannerOnWord : copy.form.scannerOffWord}
            </LabelWithTooltip>
          </div>

          <div>
            <LabelWithTooltip
              className="mb-1.5"
              tooltip={copy.tooltips.failOn}
              ariaLabel={copy.form.failOnAriaLabel}
            >
              {copy.form.failOnLabel}
            </LabelWithTooltip>
            <div className="flex gap-4">
              {SEVERITIES.map((sev) => (
                <div key={sev} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    id={`failon-${sev}`}
                    checked={scanner.failOn.includes(sev)}
                    onCheckedChange={() => toggleFailOn(sev)}
                    disabled={disabled || sev === 'error'}
                  />
                  <Label htmlFor={`failon-${sev}`} className="text-foreground">
                    {copy.severityLabels[sev]}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{copy.form.limitsCardTitle}</CardTitle>
          <CardDescription>{copy.form.limitsCardDescription}</CardDescription>
        </CardHeader>
        <CardContent>{renderNumericGroup(limitFields)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{copy.form.heuristicsCardTitle}</CardTitle>
          <CardDescription>{copy.form.heuristicsCardDescription}</CardDescription>
        </CardHeader>
        <CardContent>{renderNumericGroup(heuristicFields)}</CardContent>
      </Card>

      {scanner.rules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{copy.form.rulesCardTitle}</CardTitle>
            <CardDescription>{copy.form.rulesCardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <LabelWithTooltip
              className="mb-1.5"
              tooltip={copy.tooltips.rules}
              ariaLabel={copy.form.rulesAriaLabel}
            >
              {copy.form.rulesLabel}
            </LabelWithTooltip>
            <div className="divide-y divide-border/50 rounded-md border border-border">
              {scanner.rules.map((rule) => {
                const meta = describeRule(rule.id, copy);
                return (
                  <div
                    key={rule.id}
                    className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">
                          {meta?.title ?? rule.id}
                        </span>
                        {meta !== undefined && (
                          <span className="font-mono text-xs text-muted-foreground">{rule.id}</span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {copy.severityLabels[rule.severity]}
                        </span>
                        {isRequiredScannerRuleId(rule.id) && (
                          <span className="text-xs font-medium text-primary">
                            {copy.form.requiredRuleLabel}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {meta?.description ?? copy.form.noRuleDescription}
                      </p>
                    </div>
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={(c) => toggleRule(rule.id, c)}
                      disabled={disabled || isRequiredScannerRuleId(rule.id)}
                      aria-label={copy.form.ruleAriaLabel(meta?.title ?? rule.id)}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {canManage && (
        <div className="flex justify-end">
          <Button loading={saving} onClick={() => onSave({ fastpathEnabled, scanner })}>
            {copy.form.save}
          </Button>
        </div>
      )}
    </div>
  );
}
